// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import {
  Card as MuiCard,
  CardContent,
  CardHeader as MuiCardHeader,
} from '@mui/material';
import SolyticsBox from './SolyticsBox';

//KEY DESIGN TOKENS
const CARD_BORDER_RADIUS = 0.5; // 4px

// Default props as constants
const DEFAULT_VARIANT = 'simple';

// Custom elevation shadows
const ELEVATION_SHADOWS = {
  0: 'none',
  1: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  2: '0 2px 4px 0 rgba(0, 0, 0, 0.08)',
  3: '0 4px 8px 0 rgba(0, 0, 0, 0.12)',
};

const validateElevation = (elevation) => {
  if (elevation === undefined) return undefined;
  const clamped = Math.min(Math.max(elevation, 0), 3);
  if (elevation < 0 || elevation > 3) {
    console.warn(
      `[SolyticsCard] Invalid elevation "${elevation}". Expected a number between 0 and 3. Using clamped value: ${clamped}`
    );
  }

  return clamped;
};

// Customize it based on UI/UX updates

const SolyticsCard = ({
  children,
  variant = DEFAULT_VARIANT,
  elevation,
  cardHeader,
  headerHeight,
  disableCardHeaderWrapper = false,
  disableContentPadding = false,
  disableHeaderPadding = false,
  // Dimension props
  height,
  width,
  minHeight,
  maxHeight,
  minWidth,
  maxWidth,
  // Spacing props
  p,
  pt,
  pr,
  pb,
  pl,
  px,
  py,
  m,
  mt,
  mr,
  mb,
  ml,
  mx,
  my,
  // Layout props
  gap,
  // Color props
  bgcolor,
  scrollableContent = false,
}) => {
  // Validate elevation is between 0-3 if provided
  const validElevation = validateElevation(elevation);
  const CARD_HEADER_STYLES = (theme) => ({
    p: disableHeaderPadding ? 0 : 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    bgcolor: theme?.palette?.other?.inputBackground,
    borderBottom: `1px solid ${theme?.palette?.divider}`,
    height: headerHeight || 48,
  });

  // Build props object for MUI Card
  const cardProps = {
    elevation: 0, // Disable MUI elevation, use custom shadows
    sx: (theme) => ({
      borderRadius: CARD_BORDER_RADIUS,
      overflow: 'hidden',
      // Dimension styles
      ...(height && { height }),
      ...(width && { width }),
      ...(minHeight && { minHeight }),
      ...(maxHeight && { maxHeight }),
      ...(minWidth && { minWidth }),
      ...(maxWidth && { maxWidth }),
      // Padding styles
      ...(p !== undefined && { p }),
      ...(pt !== undefined && { pt }),
      ...(pr !== undefined && { pr }),
      ...(pb !== undefined && { pb }),
      ...(pl !== undefined && { pl }),
      ...(px !== undefined && { px }),
      ...(py !== undefined && { py }),
      // Margin styles
      ...(m !== undefined && { m }),
      ...(mt !== undefined && { mt }),
      ...(mr !== undefined && { mr }),
      ...(mb !== undefined && { mb }),
      ...(ml !== undefined && { ml }),
      ...(mx !== undefined && { mx }),
      ...(my !== undefined && { my }),
      // Color styles -  resolves theme palette colors
      ...(bgcolor && { bgcolor }),
      // Custom elevation shadow
      ...(elevation !== undefined && {
        boxShadow: ELEVATION_SHADOWS[validElevation],
      }),
      // Variant-specific styling
      ...(variant === 'outlined' && {
        borderColor: theme.palette.divider,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: CARD_BORDER_RADIUS,
      }),
      // Make sure flex children can grow if needed
      ...(scrollableContent && {
        display: 'flex',
        flexDirection: 'column',
      }),
    }),
    role: 'article',
    'aria-label': 'Card',
  };

  return (
    <MuiCard {...cardProps}>
      {/* Card Header - render custom JSX directly */}
      {cardHeader &&
        (disableCardHeaderWrapper ? (
          cardHeader
        ) : (
          <MuiCardHeader
            sx={CARD_HEADER_STYLES}
            title={cardHeader}
            // minHeight={headerHeight || 48}
          />
        ))}
      {/* Scrollable Content: If scrollableContent prop is true, render it in a Box with flex: 1, minHeight: 0, overflow: auto */}
      {scrollableContent ? (
        <SolyticsBox
          component="div"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </SolyticsBox>
      ) : (
        <CardContent
          sx={{
            ...(disableContentPadding
              ? { p: 0, '&:last-child': { pb: 0 } }
              : { '&:last-child': { paddingBottom: 3 } }),
            ...(gap !== undefined && {
              display: 'flex',
              flexDirection: 'column',
              gap,
              overflow: 'auto',
            }),
          }}
        >
          {children}
        </CardContent>
      )}
    </MuiCard>
  );
};

export default SolyticsCard;

/**
 * SolyticsCard - A wrapper around MUI Card component
 *
 * A container component for grouping related content with elevation shadows or outlined border.
 * Supports custom headers and individual styling props.
 *
 * @component
 * @example
 * // Basic card with elevation
 * <SolyticsCard elevation={2} p={3}>
 *   <p>Card content here</p>
 * </SolyticsCard>
 *
 * @example
 * // Outlined variant
 * <SolyticsCard variant="outlined" p={3}>
 *   <p>Card with border</p>
 * </SolyticsCard>
 *
 * @example
 * // Card with custom header
 * <SolyticsCard
 *   elevation={2}
 *   cardHeader={
 *     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 *       <Typography variant="h6">Card Title</Typography>
 *       <Button>Action</Button>
 *     </Box>
 *   }
 *   p={3}
 * >
 *   <p>Card content</p>
 * </SolyticsCard>
 *
 * @example
 * // Custom styled card
 * <SolyticsCard
 *   elevation={3}
 *   p={3}
 *   minWidth="300px"
 *   maxWidth="600px"
 *   bgcolor="rgba(0, 0, 0, 0.02)"
 * >
 *   <p>Styled card content</p>
 * </SolyticsCard>
 */
